/*
 * Renders the Chart Panel.
 * This has the header.
 */
import * as React from 'react';
import bind from 'bind-decorator';
import { getLocalization } from '../../global/global';
import GenericModal from '../Modals/GenericModal';
import { FormInterface } from '../../Interfaces/Forms/FormsInterface';
import { FiltersMenuInterface } from '../../Interfaces/FiltersMenuInterface';
import { LooseObject } from '../../Interfaces/LooseObject';
import { Button, Card, Form, Modal } from 'react-bootstrap';
import ChartBoxComponent from './ChartBoxComponent';
import ChartOptionsComponent from './ChartOptionsComponent';
import * as ChartUtils from './utils';
import { ModalComponentNames, ModalInterface } from '../../Interfaces/ModalInterface';
// import { ChartReportInterface } from '../../Interfaces/ChartsMenu';
// @ts-ignore
import jsPDF from 'jspdf';
import Canvg from 'canvg';
import { ClientPersistInterface } from '../../Interfaces/ClientPersistInterface';
import { ListItem } from '../../Interfaces/Forms/QuestionInterface';
import { getFormUtils } from '../SingleInstance/utils/FormUtilsHolder';
import { ChartModel, ChartResponse, SeriesData } from '../../Interfaces/ChartInterface';
import { Locations } from 'Interfaces/LocationInterface';

interface IExternalProps {
  userName: ClientPersistInterface['userName'];
  form: FormInterface;
  forms: FormInterface[];
  parentForm?: FormInterface;
  model: ChartModel;
  filtersMenu: FiltersMenuInterface;
  loadChartData: (url: string, callBack: (data) => void) => void;
  loadBarChart: (chartModel: ChartModel) => Promise<Response>;
  locationLabels: string[];
  unSelectChart: (chartId: string) => void;
  selectChart: (chartId: string) => void;
  saveChart: (chart: ChartModel, tempId?) => Promise<Response>;
  deleteChartsReport: (reportId: string) => void;
  navigateAddModal: (modal: ModalInterface) => void;
  navigateRemoveModal: (modalName: ModalComponentNames) => void;
  locations: Locations;
}

interface IOwnState {
  chartData:  ChartResponse[] | undefined;
  charts: LooseObject[] | LooseObject;
  chartKeys: string[];
  edit: boolean;
  showSaveModal: boolean;
  reportName: string;
  model: ChartModel;
  chartsSvgs: SVGElement[];
  rows: ListItem[];
  loading: boolean;
}

export default class ChartPanel extends React.Component<IExternalProps, IOwnState> {

  constructor(props) {
    super(props);
    this.state = {
      chartData: undefined,
      charts: [],
      chartKeys: [],
      edit: typeof props.model.id === 'string' && props.model.id.startsWith('new-'),
      showSaveModal: false,
      reportName: props.model.name,
      model: props.model,
      chartsSvgs: [],
      rows: this.getTableRows(),
      loading: false
    };
  }

  @bind
  private getTableRows() {
    const { parentForm, form } = this.props;
    if (parentForm && form.questionId) {
      const fu = getFormUtils(parentForm);
      return fu.getRows(form.questionId);
    }
    return [];
  }

  /*
   * Edit button click handler.
   */
  @bind
  private toggleEdit() {
    this.setState({ edit: !this.state.edit });
  }

  @bind
  private saveChart(visible) {
    const state = {};
    state['showSaveModal'] = visible;
    if (!visible) {
      state['reportName'] = '';
    }
    this.setState(state);
  }

  @bind
  private handleChange(e) {
    const state = {};
    state[e.target.name] = e.target.value;
    this.setState(state);
  }

  @bind
  private updateChartsSvgs(svg: SVGElement, id: string) {
    const {chartsSvgs} = this.state;
    chartsSvgs.push(svg);
    this.setState({chartsSvgs: chartsSvgs});
  }

  @bind
  private getSaveModalBody() {
    return (
      <Form.Group>
        <Form.Label>{getLocalization('reportName')}</Form.Label>
        <Form.Control type="text" name="reportName" value={this.state.reportName} onChange={this.handleChange} />
      </Form.Group>
    );
  }

  @bind
  private onSaveChart() {
    const { model } = this.state;
    model.name = this.state.reportName;
    const tempId = model.id;
    model.id = `${model.id}`.startsWith('new-') ? '' : model.id;
    this.setState({ showSaveModal: false });
    const savePromise = this.props.saveChart(model, tempId);
    savePromise.then((response) => response.json()).then(json => {
      const id = json.id;
      this.props.unSelectChart(`${tempId}`);
      this.props.selectChart(`${id}`);
      model.id = id;
      this.setState({ model });
    }).catch((error) => {
        console.log(error);
    });
  }

  @bind
  private onDeleteChartReport() {
    this.props.navigateAddModal({
      component: ModalComponentNames.ConfirmationModal,
      props: {
        onClose: () => this.props.navigateRemoveModal(ModalComponentNames.ConfirmationModal),
        onConfirm: () => {
          this.props.deleteChartsReport(this.props.model.id);
          this.props.navigateRemoveModal(ModalComponentNames.ConfirmationModal);
        },
        localizations: {
          cancel: getLocalization('cancel'),
          confirm: getLocalization('deletes'),
          confirmStyle: 'danger',
          header: (
              <Modal.Title>
                {getLocalization('reportConfirmDelete')}
              </Modal.Title>
          ),
          body: (
              <React.Fragment>
                {`Remove ${this.props.model.name} permanently from your report selections?`}
              </React.Fragment>
          )
        }
      }
    });
  }

  /*@bind
  private onChartSaved(response) {
    const model = {...this.state.model};
    model.id = response.message;
    this.setState({ model });
  }*/

  @bind
  private applyFilters() {
    const  { filtersMenu } = this.props;
    const model = {...this.state.model};
    const locations: string[] = [];
    const levels: string[] = [];
    filtersMenu.selectedLocations.forEach(location => {
      locations.push(`${location.key}`);
      levels.push(`${Number(location.level) + 1}`);
    });
    model.locations = locations.join(',');
    model.locationLevels = levels.join(',');
    model.fromDate = filtersMenu.selectedDates.from || '';
    model.toDate = filtersMenu.selectedDates.to || '';
    model.users = filtersMenu.selectedUsers.map(u => u.id).join(',');
    this.setState({ model }, () => this.loadChart());
  }

  /*
   * When a user presses done.
   * we set the url to the model and set edit to false.
   */
  @bind
  private onDone(url) {
    const model = {...this.state.model};
    // model.url = url;
    this.setState({ model: model, edit: false });
  }
  /*
   * Callback function for when chart data is loaded.
   */
  @bind
  private chartLoaded(data: ChartResponse[], model: ChartModel) {
    const chartKeys: string[] = [];
    const groupedCharts: any = null;
    const { form } = this.props;
    let charts;
    switch (model.type) {
      case 'BAR_CHOICE':
      case 'BAR_NUMERIC':
        charts = ChartUtils.getCharts(model, data, form, this.props.forms, this.props.locations);
        break;
      case 'PIE':
        charts = data.map((d) => {
          return ChartUtils.getPieChartObject(model, d);
        });
        break;
      case 'LINE_NUMERIC':
      case 'LINE_CHOICE':
        if (model.combine === 'FIELD') {
          charts = ChartUtils.getCombinedLineChart(model, data, undefined, this.props.locations);
        } else if (model.combine === 'LOCATION') {
          charts = ChartUtils.getCombinedLocationLineChart(model, data, undefined, this.props.locations);
        } else {
          charts = data.map((d) => ChartUtils.getLineChart(model, d, undefined, this.props.locations));
        }
        break;
      default:
        charts = [];
    }
    this.setState({
      chartData: data,
      charts: groupedCharts || charts,
      chartKeys,
      chartsSvgs: [],
      model
    });
  }

  @bind
  private switchChart(id: string, action: boolean, name?: string) {
    const { chartData, charts, model } = this.state;
    const { forms, form } = this.props;
    if (chartData) {
      if (name === 'switch') {
        if (model.type === 'BAR_NUMERIC' && model.combine) {
          if (action) {
            const c = ChartUtils.switchNumericCharts(chartData, model, this.props.locations);
            let switchedCharts = [];
            if (Array.isArray(c)) {
              if (model.stack) {
                const seriesData: SeriesData[] = [];
                c.forEach(cc => {
                  cc.seriesData.forEach(sd => {
                    sd.stack = sd.name;
                    sd.name = cc.title;
                    seriesData.push(sd);
                  });
                });
                const categories = c[0].categories;
                switchedCharts = switchedCharts.concat(
                  ChartUtils.getCombinedCharts({ categories, seriesData }, model, undefined)
                );
              } else {
                c.forEach(cc => {
                  switchedCharts = switchedCharts.concat(ChartUtils.getCombinedCharts(cc, model, undefined, cc.title));
                });
              }
            } else {
              switchedCharts = ChartUtils.getCombinedCharts(c, model, undefined);
            }
            this.setState({ charts: switchedCharts });
          } else {
            const switchedCharts = ChartUtils.getCharts(model, chartData, form, this.props.forms, this.props.locations);
            this.setState({ charts: switchedCharts });
          }
        } else {
          let chart = chartData.find((cd) => cd.id === id);
          if (chart) {
            if (action) {
              chart = ChartUtils.switchChart(chart);
            }
            const readyChart = ChartUtils.getCharts(model, [chart], form, forms);
            const newCharts = charts.map((ch) => {
              if (ch.responseId !== id) {
                return ch;
              }
              if (readyChart.length > 0) {
                const r = readyChart.splice(0, 1);
                return r[0];
              }
              return null;
            }).filter(c => c !== null);
            this.setState({ charts: newCharts });
          }
        }
        return;
      } else if (name === 'cummulative') {
        if (model.combine === 'FIELD') {
          const charts = ChartUtils.getCombinedLineChart(model, chartData, action, this.props.locations);
          this.setState({ charts });
        } else if (model.combine === 'LOCATION') {
          const charts = ChartUtils.getCombinedLocationLineChart(model, chartData, action, this.props.locations);
          this.setState({ charts });
        } else {
          const chart = chartData.find((cd) => cd.id === id);
          if (chart) {
            const newChart = ChartUtils.getLineChart(model, chart, action);
            const newCharts = charts.map((ch) => {
              if (ch.responseId === id) {
                return newChart;
              }
              return ch;
            });
            this.setState({ charts: newCharts });
          }
        }
        return;
      }
    }
  }

  @bind
  private loadChart() {
    const { model } = this.state;
    const loadPromise = this.props.loadBarChart(model);
    loadPromise.then(response => response.json()).then(json => {
      this.chartLoaded(json, model);
    }).catch(error => {
      console.log(error);
    });
  }

  /*
   * if this is an existing chart then we load it.
   */
  public componentDidMount() {
    const { model } = this.state;
    if (model) {
      if (model.id && !`${model.id}`.startsWith('new')) {
        this.loadChart();
      }
      // const url = ChartUtils.getRequestUrl(model);
      // this.props.loadChartData(url, (data) => this.chartLoaded(data, ChartUtils.deconstructURL(model, {})));
    }
  }

  private static async svgToPNG(svg, startX: number) {
    let width = parseInt(svg.match(/width="([0-9]+)"/)[1], 10);
    let height = parseInt(svg.match(/height="([0-9]+)"/)[1], 10);
    const aspectRatio = height / width;
    if ((width + 2 * startX) > 595) {
      // If width goes beyond page width, adjust image with aspect ratio
      width = 595 - 2 * startX;
      height = aspectRatio * width;
    }
    const canvasEl: HTMLCanvasElement = document.createElement<'canvas'>('canvas');
    canvasEl.setAttribute('width', width + '');
    canvasEl.setAttribute('height', height + '');
    const ctx: CanvasRenderingContext2D = canvasEl.getContext('2d') as CanvasRenderingContext2D;
    const canvasCreated = await Canvg.from(ctx, svg, {ignoreMouse: true, ignoreAnimation: true});
    await canvasCreated.render();
    return {
      src: canvasEl.toDataURL('image/png'),
      width,
      height
    };
  }

  private static async addPageImage(parameters: {
    // @ts-ignore
    doc: jsPDF,
    svg: SVGElement,
    pageNumber: number,
    startX: number,
    startYpos: number,
    generatedBy: string,
    text: string
  }): Promise<boolean> {
    const {doc, svg, pageNumber, startX, startYpos, generatedBy, text} = parameters;
    const maxWidth = 595.28;
    const maxHeight = 841.89;
    if (pageNumber > 0) {
      doc.addPage();
      doc.setFontSize(10);
    }
    doc.text(text, startX, startYpos);
    const startY = startYpos + 20;
    const imgData = await ChartPanel.svgToPNG(svg, startX);
    doc.addImage(imgData.src, 'PNG', startX, startY, imgData.width, imgData.height);
    doc.setFontSize(8);
    doc.text(generatedBy, 10, maxHeight - 10);
    doc.text((pageNumber + 1).toString(), maxWidth - 20, maxHeight - 10);
    return true;
  }

  private async downloadPdf() {
    const startX = 40;
    const startY = 20;
    const generatedBy = `Generated by ${this.props.userName} on ${new Date().toDateString()}`;
    // @ts-ignore
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'pt',
      format: 'a4' // [595.28, 841.89]
    });
    doc.text(this.props.model.name, startX, startY);
    const imgStartY = startY + 20;
    const chartSvgs = this.state.chartsSvgs;
    for (let i = 0; i < chartSvgs.length; i++) {
      await ChartPanel.addPageImage({
        doc: doc,
        svg: chartSvgs[i],
        pageNumber: i,
        startX: startX,
        startYpos: imgStartY,
        generatedBy: generatedBy,
        text: ''
      });
    }
    doc.save(`${this.props.model.name}.pdf`);
  }

  @bind
  private getChartActions() {
    const { unSelectChart } = this.props;
    return (
      <React.Fragment>
        <Button className="close" aria-label="Close" onClick={() => unSelectChart(`${this.state.model.id}`)}>
          <span aria-hidden="true">&times;</span>
        </Button>
        {!this.state.edit && (
            <div className="chart-action-btn">
              <Button size="sm" variant="info" onClick={this.toggleEdit}>
                <i className="fa fa-pencil"/>
              </Button>
              <Button size="sm" variant="info" onClick={this.applyFilters}>
                <i className="fa fa-refresh"/>
                {getLocalization('apply_filters')}
              </Button>
              <Button
                  variant="danger"
                  size="sm"
                  onClick={() => this.downloadPdf()}
              >
                <i className="fa fa-file-pdf-o"/>
              </Button>
              <Button size="sm" variant="info" onClick={() => this.saveChart(true)}>
                <i className="fa fa-floppy-o"/>
              </Button>
              <Button size="sm" variant="danger" onClick={() => this.onDeleteChartReport()}>
                <i className="fa fa-trash-o"/>
              </Button>
            </div>
        )}
      </React.Fragment>
    );
  }

  public render() {
    const { charts, model, showSaveModal, edit } = this.state;
    const saveModal = showSaveModal ? (
      <GenericModal
        visible={this.state.showSaveModal}
        body={this.getSaveModalBody()}
        onConfirm={this.onSaveChart}
        cancel={() => this.saveChart(false)}
        title={getLocalization('reportsFormat')}
        confirmText={getLocalization('save')}
        cancelText={getLocalization('cancel')}
      />
    ) : null;

    /*const getCharts = (list, key) => {
      const chartList = list.map((chart, index) => {
        return (
          <ChartBoxComponent
            key={`${model.id}-chart-${key ? key : ''}-${chart['id']}`}
            id={chart['id']}
            index={index}
            chartType={model.type}
            options={chart}
            switchChart={this.switchChart}
            addChartSvgs={this.updateChartsSvgs}
            model={model}
          />
        );
      });
      if (key) {
        return (
          <Card key={`${key}-chart-card`}>
            <Card.Header>{key}</Card.Header>
            <Card.Body>
              <div className="row">
                {chartList}
              </div>
            </Card.Body>
          </Card>
        );
      }
      return chartList;
    };*/
    const chartBox: any = (
      <div className="row" >
        {charts.map((chart, index) => {
          return (
            <ChartBoxComponent
              key={`${model.id}-chart-${chart['id']}-${index}-${chart['responseId']}`}
              id={chart['responseId']}
              index={index}
              chartType={model.type}
              options={chart}
              switchChart={this.switchChart}
              addChartSvgs={this.updateChartsSvgs}
              model={model}
            />
          );
        })}
      </div>
    );
    /*Array.isArray(charts) ? (
      chartBox[0] = (
        <div className="row" key={`${model.id}_chart_box`}>
          {getCharts(charts, false)}
        </div>
      )
    ) : (
      forOwn(charts, (chart, key) => {
        chartBox = chartBox.concat(getCharts(chart, key));
      })
    );*/
    return (
      <Card className="chart-panel">
        {saveModal}
        <Card.Header>
          <span className="report-title">{model.name}</span>
          {this.getChartActions()}
        </Card.Header>
        <Card.Body>
          {edit && (
            <ChartOptionsComponent
              form={this.props.form}
              model={this.state.model}
              loadChartData={this.props.loadChartData}
              loadBarChart={this.props.loadBarChart}
              locationLabels={this.props.locationLabels}
              chartLoaded={this.chartLoaded}
              filtersMenu={this.props.filtersMenu}
              onDone={this.onDone}
            />
          )}
          {chartBox}
        </Card.Body>
      </Card>
    );
  }
}
